// Wishlist Adoption Modal (B3.1, Feb 2026).
//
// One-shot shadcn Dialog mounted at /app level for non-internal users.
// Lifecycle:
//   1. mount → useWlpAdoption fetches eligibility (server authoritative).
//   2. if showModal=true → render dialog and POST `shown` event.
//   3. on "Set up my wishlist" → POST `clicked` + navigate to /app/settings#wlp.
//   4. on "Maybe later" / X → POST `dismissed` + snooze 14d server-side.

import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "@/i18n";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Sparkles } from "lucide-react";
import useWlpAdoption from "./useWlpAdoption";

export default function WishlistAdoptionModal() {
  const { t } = useLang();
  const location = useLocation();
  const { showModal, disabled, emit, snooze } = useWlpAdoption();
  const seenRef = useRef(false);

  // Never show the modal on the Settings page itself — the banner does
  // the same job there.
  const onSettings = location.pathname.startsWith("/app/settings");
  const open = !disabled && !onSettings && showModal;

  // Emit `shown` exactly once per modal lifetime (the hook also guards
  // against double-emits across re-renders).
  useEffect(() => {
    if (open && !seenRef.current) {
      seenRef.current = true;
      emit("shown");
    }
  }, [open, emit]);

  const handleDismiss = async () => {
    emit("dismissed");
    await snooze();
  };

  const handleClick = () => {
    emit("clicked");
    // Use window.location.assign for a deterministic URL+hash update.
    // react-router's navigate() proved unreliable inside the dialog
    // context (testing_agent_v3_fork iteration_16 confirmed). A hard
    // navigate is acceptable here because the adoption funnel is a
    // top-level conversion path, not a deeply-nested SPA route.
    window.location.assign("/app/settings#wishlist-preferences");
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleDismiss(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="wlp-adoption-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 text-emerald-700">
            <Sparkles className="h-5 w-5" />
            <DialogTitle data-testid="wlp-adoption-modal-heading">
              {t("wlpAdoption.modalHeading")}
            </DialogTitle>
          </div>
          <DialogDescription className="text-slate-700 mt-2 leading-relaxed">
            {t("wlpAdoption.modalBody")}
          </DialogDescription>
        </DialogHeader>

        <div
          className="rounded-md border border-emerald-100 bg-emerald-50 p-3 text-xs text-emerald-900 flex gap-2"
          data-testid="wlp-adoption-modal-privacy"
        >
          <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold mb-0.5">{t("wlpAdoption.privacyHeading")}</div>
            <div>{t("wlpAdoption.privacyBody")}</div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={handleDismiss}
            data-testid="wlp-adoption-modal-dismiss"
          >
            {t("wlpAdoption.modalCtaSecondary")}
          </Button>
          <Button
            onClick={handleClick}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="wlp-adoption-modal-cta"
          >
            {t("wlpAdoption.modalCtaPrimary")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
